import { useEffect, useState } from 'react';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/lib/stores';
import { AppStoreBadge, PlayStoreBadge } from '@/components/StoreBadges';

/**
 * /get — smart-redirect to the right store.
 *
 * Used as the QR-code payload on the PaywallSheet's desktop variant
 * so a phone scan lands on the correct platform's store without the
 * desktop user having to choose. Also linkable from any marketing
 * surface as "the canonical install link".
 *
 * On iOS UA → 302 to App Store via window.location.replace.
 * On Android UA → 302 to Play Store.
 * On everything else (desktop, watchOS, weird crawlers) → render a
 * fallback page with both store badges so the user can pick.
 *
 * Detection is intentionally simple regex (no UA-parser dep) — the
 * three cases we care about are well-served by it, and the fallback
 * page is graceful for anything misclassified.
 */
export function GetPage() {
  // Render-fallback state. Starts as 'detecting' to avoid a flash of
  // the badges before the redirect fires on real iOS/Android devices.
  const [state, setState] = useState<'detecting' | 'fallback'>('detecting');

  useEffect(() => {
    if (typeof navigator === 'undefined') {
      setState('fallback');
      return;
    }
    const ua = navigator.userAgent || '';
    // iPad now reports itself as macOS — guard with maxTouchPoints
    // so iPadOS users get the App Store and not the desktop fallback.
    const isIPad = /Macintosh/.test(ua) && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints !== undefined && ((navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints ?? 0) > 1;
    if (/iPhone|iPod/.test(ua) || isIPad) {
      // Use replace so the user's back button doesn't return them to
      // /get, which would just re-redirect.
      window.location.replace(APP_STORE_URL);
      return;
    }
    if (/Android/.test(ua)) {
      window.location.replace(PLAY_STORE_URL);
      return;
    }
    // Desktop, ChromeOS, smart fridge, etc. → fallback UI.
    setState('fallback');
  }, []);

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--bg)',
      }}
    >
      <div
        style={{
          maxWidth: 420,
          width: '100%',
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 24,
          padding: 28,
          textAlign: 'center',
        }}
      >
        <div
          className="mono"
          style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}
        >
          WYDM
        </div>
        <h1
          className="serif"
          style={{ fontSize: 32, fontWeight: 500, fontStyle: 'italic', margin: 0, lineHeight: 1.1 }}
        >
          Get the app
        </h1>
        <p style={{ color: 'var(--ink-3)', fontSize: 14, marginTop: 12, lineHeight: 1.5 }}>
          {state === 'detecting'
            ? 'Opening the store…'
            : 'Pick your platform — installs take about 30 seconds.'}
        </p>

        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          <AppStoreBadge href={APP_STORE_URL} height={52} />
          <PlayStoreBadge href={PLAY_STORE_URL} height={52} />
        </div>

        <div style={{ marginTop: 22, fontSize: 11, color: 'var(--ink-3)' }}>
          Already have it?{' '}
          <a href="/home" style={{ color: 'var(--ink-2)', textDecoration: 'underline' }}>
            Continue on the web
          </a>
        </div>
      </div>
    </main>
  );
}
